import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Card, Empty, Spin, Typography } from "antd";
import { find, first, isNil, map } from "lodash";
import { DropdownInput } from "components";
import { EMPTY } from "constants/common";
import { AiccLanguage, AiccSubUserDetail as AiccSubUserDetailModel } from "types/model/aiccCustomer";
import { CustomerDetailsTab } from "types/model/customer";
import * as aiccAPI from "services/aiccCustomer";
import { useAICCStore } from "../../../useAICCStore";
import PreviewTag from "../PreviewTag";
import Onboarding from "./Onboarding";
import LearningAssets from "./LearningAssets";
import ScenarioConversation from "./ScenarioConversation";
import LessonReview from "./LessonReview";
import { SubUserDetailTab } from "./constants";
import styles from "./SubUserDetail.module.scss";

const tabList = map(SubUserDetailTab, (tab) => ({ key: tab, tab }));

function SubUserDetail() {
  const { subUserId } = useParams<{ subUserId: string }>();
  const history = useHistory();
  const setPendingCustomerDetailTab = useAICCStore((state) => state.setPendingCustomerDetailTab);
  const subUserDetailTab = useAICCStore((state) => state.subUserDetailTab);
  const setSubUserDetailTab = useAICCStore((state) => state.setSubUserDetailTab);
  const setSubUserDetailLanguage = useAICCStore((state) => state.setSubUserDetailLanguage);
  const getCachedSubUser = () => {
    const cache = useAICCStore.getState().cachedSubUserDetail;
    return cache?.id === subUserId ? cache : undefined;
  };
  const getCachedLanguageId = () => {
    const cache = useAICCStore.getState().subUserDetailLanguage;
    return cache?.subUserId === subUserId ? cache.languageId : undefined;
  };
  const resolveLanguageId = (studyLanguages?: AiccLanguage[], preferredId?: number) =>
    find(studyLanguages, { id: preferredId }) ? preferredId : first(studyLanguages)?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [subUser, setSubUser] = useState<AiccSubUserDetailModel | undefined>(getCachedSubUser);
  const [languageId, setLanguageId] = useState<number | undefined>(() =>
    resolveLanguageId(getCachedSubUser()?.studyLanguages, getCachedLanguageId())
  );
  const activeTabKey = subUserDetailTab?.subUserId === subUserId ? subUserDetailTab.tab : SubUserDetailTab.Onboarding;
  const handleTabChange = (tabKey: string) => setSubUserDetailTab({ subUserId, tab: tabKey as SubUserDetailTab });
  const handleLanguageChange = (selectedLanguageId: number) => {
    setLanguageId(selectedLanguageId);
    setSubUserDetailLanguage({ subUserId, languageId: selectedLanguageId });
  };

  useEffect(() => {
    const cachedSubUser = getCachedSubUser();
    const cachedLanguageId = getCachedLanguageId();
    setSubUser(cachedSubUser);
    setLanguageId(resolveLanguageId(cachedSubUser?.studyLanguages, cachedLanguageId));
    setIsLoading(isNil(cachedSubUser));

    let isStale = false;
    aiccAPI
      .fetchAiccSubUserDetail(subUserId)
      .then((subUserDetail) => {
        if (isStale) {
          return;
        }
        setSubUser(subUserDetail);
        setLanguageId((currentLanguageId) =>
          resolveLanguageId(subUserDetail?.studyLanguages, currentLanguageId ?? cachedLanguageId)
        );
      })
      .catch(() => !isStale && isNil(cachedSubUser) && setSubUser(undefined))
      .finally(() => !isStale && setIsLoading(false));

    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subUserId]);

  const languageOptions = useMemo(() => map(subUser?.studyLanguages, ({ id, name }) => ({ label: name, value: id })), [
    subUser,
  ]);

  const languageName = find(languageOptions, { value: languageId })?.label;

  const handleCustomerEmailClick = (customerId: number) => {
    setPendingCustomerDetailTab(CustomerDetailsTab.AICC);
    history.push(`/customers/b2c/${customerId}`);
  };

  return (
    <Spin spinning={isLoading}>
      <Card
        title={
          <div className={styles.header}>
            <div className={styles.title}>
              {subUser?.isPreview && <PreviewTag className={styles.previewTag} />}
              {subUser ? `${subUser.name} (${subUser.id})` : subUserId}
            </div>
            <div>
              {subUser?.customerEmail ? (
                <Typography.Link onClick={() => handleCustomerEmailClick(subUser.customerId)}>
                  {subUser.customerEmail}
                </Typography.Link>
              ) : (
                EMPTY
              )}
            </div>
            <div className={styles.language}>
              <span className={styles.languageLabel}>Languages:</span>
              <div className={styles.languageSelect}>
                <DropdownInput
                  options={languageOptions}
                  value={languageId}
                  onChange={handleLanguageChange}
                  placeholder="Language"
                  mode="single"
                />
              </div>
            </div>
          </div>
        }
        className="page-container"
        tabList={tabList}
        activeTabKey={activeTabKey}
        onTabChange={handleTabChange}
      >
        {activeTabKey === SubUserDetailTab.Onboarding &&
          (languageId ? (
            <Onboarding subUserId={subUserId} languageId={languageId} languageName={languageName} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ))}
        {activeTabKey === SubUserDetailTab.LearningAssets &&
          (languageId ? (
            <LearningAssets subUserId={subUserId} languageId={languageId} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ))}
        {activeTabKey === SubUserDetailTab.ScenarioConversation &&
          (languageId ? (
            <ScenarioConversation subUserId={subUserId} languageId={languageId} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ))}
        {activeTabKey === SubUserDetailTab.LessonReview &&
          (languageId ? (
            <LessonReview subUserId={subUserId} languageId={languageId} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ))}
      </Card>
    </Spin>
  );
}

export default SubUserDetail;
